import React, { useCallback, useRef, useState } from 'react';
import resetPasswordSx from './ResetPassword.styles';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Card, FormControl, Grid, Input, Link, Typography } from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import api from 'api';
import useRequest from 'hooks/useRequest';

function verifyPasswordResetToken() {
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');
    const token = searchParams.get('token');

    return api.get('/auth/reset-password', { params: { email, token } });
}

const ResetPassword = () => {
    const theme = useTheme();
    const sx = resetPasswordSx(theme);
    const classes = sx,
        formRef = useRef(null),
        [password, setPassword] = useState(''),
        [repeatPassword, setRepeatPassword] = useState('');

    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const handleChangePassword = useCallback(() => {
        return api.patch('/auth/reset-password', { password, token, email });
    }, [password, token, email]);

    const {
        loading: verifyingToken,
        hasLoaded: tokenVerified,
        error: tokenVerificationError,
    } = useRequest({ requestFunction: verifyPasswordResetToken });

    const {
        loading: savingPassword,
        hasLoaded: passwordSavedSuccessfully,
        error: setPasswordError,
        refetch: changePassword,
        data: setPasswordResponse,
    } = useRequest({ requestFunction: handleChangePassword, lazy: true });

    const errorMessages = setPasswordError?.response?.data?.messages ?? [];
    const hasErrors = Array.isArray(errorMessages) && errorMessages.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        await changePassword();
    };

    const disabled = !password || password !== repeatPassword || password.length < 12;

    return (
        <Box sx={sx.root}>
            <Box sx={sx.child}>
                <Grid container spacing={0} sx={sx.fullHeightWidth}>
                    <Grid size={{ xs: 12, md: 7 }} sx={sx.rightCardWrapper}>
                        <Card sx={sx.rightCard} raised>
                            {verifyingToken && 'Verifying request...'}

                            {Boolean(tokenVerificationError) && (
                                <>
                                    <Typography>
                                        {tokenVerificationError?.response?.data?.message ?? 'There was an error '}
                                    </Typography>
                                </>
                            )}

                            {tokenVerified && !passwordSavedSuccessfully && (
                                <>
                                    <form onSubmit={handleSubmit} ref={formRef}>
                                        {Boolean(tokenVerificationError) && (
                                            <Typography>
                                                {tokenVerificationError?.response?.data?.message ??
                                                    'There was an error '}
                                            </Typography>
                                        )}

                                        {hasErrors && (
                                            <Box>
                                                <Typography>There was an error updating your password.</Typography>
                                                <ul>
                                                    {errorMessages.map((message) => (
                                                        <li key={message}>{message}</li>
                                                    ))}
                                                </ul>
                                            </Box>
                                        )}

                                        <Typography>
                                            Enter your new password below. Your new password must be at least{' '}
                                            <strong>12</strong> characters in length.
                                        </Typography>

                                        <FormControl fullWidth margin="normal">
                                            <Input
                                                type="password"
                                                placeholder="New Password"
                                                onChange={(e) => setPassword(e.target.value)}
                                                value={password}
                                                sx={sx.inputs}
                                                disabled={false}
                                            />
                                        </FormControl>

                                        <FormControl fullWidth margin="normal">
                                            <Input
                                                type="password"
                                                placeholder="Repeat New Password"
                                                onChange={(e) => setRepeatPassword(e.target.value)}
                                                value={repeatPassword}
                                                sx={sx.inputs}
                                                disabled={false}
                                            />
                                        </FormControl>

                                        <FormControl fullWidth margin="normal">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={sx.inputs}
                                                type="submit"
                                                disabled={disabled || savingPassword}
                                            >
                                                {disabled
                                                    ? 'Enter your new password twice, at least 12 characters'
                                                    : 'Set Password'}
                                            </Button>
                                        </FormControl>
                                    </form>
                                </>
                            )}

                            {passwordSavedSuccessfully && (
                                <>
                                    <Typography sx={sx.marginBottom}>
                                        Your password has been updated successfully. You may now return to the{' '}
                                        <Link component={RouterLink} to="/" color="inherit">
                                            login
                                        </Link>{' '}
                                        page to log in using your new password.
                                    </Typography>
                                </>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default ResetPassword;
